import { GatewayConnectionService } from '../../services/apiHandler/gateway-connection/gateway-connection.service';
import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DidDataRetrievalService, t_DataIdentifier, t_DataPoint } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';

interface TableData {
    ecuId: number;
    didId: number;
    value?: number;
    action?: string;
    computed?: number;
  }

@Component({
    selector: 'app-connection-manager',
    templateUrl: './connection-manager.component.html',
    styleUrls: ['./connection-manager.component.scss'],
})
export class ConnectionManagerComponent implements OnDestroy {
    public ip: string = '-1';
    public port: number = -1;
    public newIp: string = '';
    public newPort: number | null = null;
    public backendAlive: boolean | null = null;
    public gatewayConnectionAlive: boolean | null = null;
    public aliveCheckInterval: number;
    private aliveCheckSub: Subscription;
    public inputErrorMsg: string = '';
    public inputError: boolean = false;

    public ecus = [{value: 0, name: '0 (Engine)'}, {value: 120, name: '120 (Master)'}, {value: 121, name: '121 (Slave)'}, {value: 122, name: '122 (Cabine)'}];
    public did: number | undefined;
    public dataIdentifierList: t_DataIdentifier[] = [];
    @ViewChild(MatTable, {static: false}) table! : MatTable<any>
    dataSource: MatTableDataSource<TableData> = new MatTableDataSource<TableData>;
    public displayColumns = ['ecu', 'did', 'value','computed', 'action'];
    started: boolean = false;

    private destroy$: Subject<void> = new Subject<void>();

    ecu = undefined;

    fetched: boolean = false;
    diagnosticInfo: DiagnosticInfo[] = [];
    columnsToDisplay = ['user', 'activeSince', 'lastRequest', 'Machinenumber', 'connectedSince'];
    columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
    expandedElement!: DiagnosticInfo | null;
    @ViewChild(MatTable, {static: false}) diagnosticTable! : MatTable<any>;    

    constructor(public apiGwConnection: GatewayConnectionService, private snackBar: MatSnackBar, private retrievalService: DidDataRetrievalService, private configService: DidConfigService, private ref:ChangeDetectorRef) {
        this.aliveCheckInterval = 5000;
        this.checkBackendAlive();
        this.checkGatewayAlive();
        //this.checkMachineStatus();
        this.aliveCheckSub = interval(this.aliveCheckInterval).subscribe(() => {
            this.checkBackendAlive();
            this.checkGatewayAlive();
            //this.checkMachineStatus();
        });
    }

    ngOnDestroy(): void {
        this.stopPeriodicAliveCheck();
        this.destroy$.next();
        this.destroy$.complete();
        this.retrievalService.stopSendQueryAliveState();
        this.retrievalService.stopDataQuery();
        this.dataIdentifierList.forEach(data => {
            this.configService.removeDidFromQueryList({ecu_id: data.ecu_id, data_id: data.data_id}).subscribe();
        });
    }

    public stopPeriodicAliveCheck(): void {
        this.aliveCheckSub.unsubscribe();
    }

    public checkBackendAlive(): void {
        this.apiGwConnection.checkBackendAlive().subscribe({
            next: (isAlive) => {
                this.backendAlive = isAlive;
            },
            error: (error) => {
                console.error('Connection Manager: Error while checking Backend Connection:', error);
            },
        });
    }

    public connectToGateway(): void {
        this.apiGwConnection.connectToGateway().subscribe((data): void => {
            this.checkGatewayAlive();
        });
    }

    public disconnectFromGateway(): void {
        this.apiGwConnection.disconnectFromGateway().subscribe((data): void => {
            interface apiResponse {
                Lookup: string;
            }
            this.checkGatewayAlive();
        });
    }

    public checkGatewayAlive(): void {
        this.apiGwConnection.checkGatewayAlive().subscribe({
            next: (isAlive) => {
                this.gatewayConnectionAlive = isAlive;
            },
            error: (error) => {
                console.error('Error while checking Gateway Conneciton:', error);
            },
        });
    }

    public checkMachineStatus() {
        this.apiGwConnection.fetchStatus();
    }

    public addValuePair() {
        let ecuId: number = Number(this.ecu);
        let didId: number = this.did!;
        let identifier = {ecu_id: ecuId, data_id: didId}
        this.dataIdentifierList.push(identifier);
        this.dataSource.data.push({ecuId: ecuId, didId: didId});
        this.did = undefined;
        this.ecu = undefined;
        this.ecus.values
        this.table?.renderRows();
        this.configService.addDidToQueryList(identifier).subscribe();
    }

    public removeValuePair(row: any) {
        this.dataIdentifierList.splice(this.dataIdentifierList.indexOf(row),1);
        this.dataSource.data.splice(this.dataSource.data.indexOf(row), 1);
        this.configService.removeDidFromQueryList({ecu_id: row.ecuId, data_id: row.didId}).subscribe();
        this.table?.renderRows();
    }

    public startDataFetching() {
        this.started = true;
        this.retrievalService.initService();
        this.retrievalService.startDataQuery();
        this.startPeriodicPolling();
    }

    private startPeriodicPolling() {
        interval(2000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.getData();
            });
    }

    private getData() {
        this.retrievalService.getAllDidsValues().subscribe((res) => {
            let response: t_DataPoint[] = JSON.parse(JSON.stringify(res.body));
            if(response.length > 0) {
                this.dataSource.data.forEach((set) => {                    
                    let data = response.find((obj) => obj.identifier.ecu_id === set.ecuId && obj.identifier.data_id === set.didId)?.data;
                    set.value = data?.value[0];

                    let extra = this.configService.getDidInformation({ecu_id: set.ecuId, data_id: set.didId});
                    let computed = this.normalizedValue(data!.value[0], extra!.conversionFactor, extra!.conversionOffset);
                    set.computed = computed;
                });
            }
        });
        this.table?.renderRows();
    }

    private normalizedValue(value: number, factor: number, offset: number) {
        if(factor == 0) {
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        return res;
    }

    public stopDataFetching(){
        this.started = false;
        this.destroy$.next();
        this.destroy$.complete();
        this.retrievalService.stopSendQueryAliveState();
        this.retrievalService.stopDataQuery();
    }

    /** Checks whether an element is expanded. */
    isExpanded(element: DiagnosticInfo) {
        return this.expandedElement === element;
    }

    /** Toggles the expanded state of an element. */
    toggle(element: DiagnosticInfo) {
        this.expandedElement = this.isExpanded(element) ? null : element;
    }

    public fetchDiagnosticInfo() {
        this.fetched = true;
        this.diagnosticInfo = [];
        this.apiGwConnection.fetchDiagnosticSessionInfo().subscribe((res) => {
            let info = JSON.parse(JSON.stringify(res));
            for(let i = 0; i < info.length; i++){
                let entry: DiagnosticInfo = {
                    user: info[i].sessionInfo.userName,
                    activeSince: info[i].sessionInfo.activeSinceTs,
                    lastRequest: info[i].sessionInfo.lastRequestTs,
                    sessionId: info[i].sessionInfo.sessionId,
                    Machinenumber: info[i].machineInfo.machineNum!,
                    commonName: info[i].machineInfo.commonName,
                    connectedSince: info[i].machineInfo.connectedSince,
                    diagSessionId: info[i].machineInfo.diagSessionId
                }
                this.diagnosticInfo.push(entry);
                this.diagnosticTable?.renderRows();
            }
        });
    }
}

export interface DiagnosticInfo {
    user: string;
    activeSince: number;
    lastRequest: number;
    sessionId: number;
    Machinenumber: string;
    commonName: string;
    connectedSince: number;
    diagSessionId: number;
}
